import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { PlatformBadge, StatusBadge, STATUS_META, PLATFORM_META, inr } from "../platforms";
import { Download, Loader2 } from "lucide-react";

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");

  const downloadPdf = async (e, inv) => {
    e.stopPropagation();
    setDownloading(inv.id);
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.invoice_no.replace(/\//g, "-") + ".pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); } finally { setDownloading(""); }
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (platform) params.platform = platform;
    api.get("/invoices", { params }).then((r) => setInvoices(r.data)).finally(() => setLoading(false));
  }, [status, platform]);

  const selCls = "border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20";

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Records</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Invoices</h1>
      </div>
      <div className="flex flex-wrap gap-3">
        <select data-testid="filter-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={selCls}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="filter-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className={selCls}>
          <option value="">All platforms</option>
          {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-[#0A192F] text-white text-left">
              {["Invoice No.", "Platform", "Date", "Bill To", "Amount", "Status", "IRN / EWB", "PDF"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="invoices-table-body" className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-slate-400">Loading...</td></tr>}
            {!loading && invoices.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-slate-400">No invoices found.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_no}`} onClick={() => navigate(`/invoices/${inv.id}`)}
                className="hover:bg-slate-50 cursor-pointer transition-colors duration-200">
                <td className="px-4 py-3 font-mono font-semibold text-slate-900 whitespace-nowrap">{inv.invoice_no}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-2"><PlatformBadge platform={inv.platform} size={13} />{PLATFORM_META[inv.platform]?.name}</span></td>
                <td className="px-4 py-3 whitespace-nowrap">{inv.invoice_date}</td>
                <td className="px-4 py-3 max-w-52 truncate">{inv.bill_to?.name}</td>
                <td className="px-4 py-3 font-mono text-right whitespace-nowrap">{inr(inv.totals?.grand_total)}</td>
                <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                  {inv.einvoice?.irn ? "IRN ✓" : "—"} {inv.ewaybill?.ewb_no ? " EWB ✓" : ""}
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`row-download-${inv.invoice_no}`} onClick={(e) => downloadPdf(e, inv)} disabled={downloading === inv.id}
                    title="Download PDF"
                    className="p-1.5 rounded-full text-slate-400 hover:text-[#0047AB] hover:bg-blue-50 transition-colors duration-200 disabled:opacity-50">
                    {downloading === inv.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
